import { type ColDef, type ColGroupDef, type IServerSideGetRowsRequest } from 'ag-grid-community';
import {
  cellStyleFunction,
  headerClassFunction,
  headerValueGetterFunction,
  pivotColumnLabelsClassFunction,
  tooltipValueGetterFunction,
  valueGetterFunction,
} from '../views/agGridReact/helpers/ColDefHelpers';
import { ValueRenderer } from '../views/agGridReact/ValueRenderer';
import { type CustomColDefContext } from 'common/types/agGrid/context';
import { PIVOT_SEPARATOR } from '../views/agGridReact/Constants';
import { type Vif } from '../vif';
import { getDatasetUid, getWrapHeaders } from './VifSelectors';

export function createPivotResultColumns(
  agRequest: IServerSideGetRowsRequest,
  pivotFields: string[],
  vif: Vif,
): (ColDef | ColGroupDef)[] {
  if (agRequest.pivotMode && agRequest.pivotCols.length > 0) {
    const datasetUid = getDatasetUid(vif);
    const wrapHeaders = getWrapHeaders(vif);
    const pivotResultCols: ColGroupDef[] = [];
    pivotFields.forEach((field) =>
      addColDef(field, field.split(PIVOT_SEPARATOR), pivotResultCols, agRequest, datasetUid, wrapHeaders, 0),
    );
    return pivotResultCols;
  }
  return [];
}

function addColDef(
  colId: string,
  parts: string[],
  pivotResultCols: (ColDef | ColGroupDef)[],
  agRequest: IServerSideGetRowsRequest,
  datasetUid: string,
  wrapHeaders: boolean,
  depth: number,
): (ColDef | ColGroupDef)[] {
  if (parts.length === 0) {
    return [];
  }

  const first = parts[0];
  const existingGroup = findExistingGroup(pivotResultCols, first);

  if (existingGroup) {
    existingGroup.children = addColDef(
      colId,
      parts.slice(1),
      existingGroup.children,
      agRequest,
      datasetUid,
      wrapHeaders,
      depth + 1,
    );
  } else {
    const newColDef = createColDef(colId, parts, agRequest, datasetUid, wrapHeaders, depth);
    const children = addColDef(colId, parts.slice(1), [], agRequest, datasetUid, wrapHeaders, depth + 1);

    if (children.length > 0) {
      newColDef.children = children;
    }

    pivotResultCols.push(newColDef);
  }

  return pivotResultCols;
}

function findExistingGroup(res: (ColDef | ColGroupDef)[], groupId: string): ColGroupDef | undefined {
  return res.find((r) => 'groupId' in r && r.groupId === groupId) as ColGroupDef;
}

function createColDef(
  colId: string,
  parts: string[],
  agRequest: IServerSideGetRowsRequest,
  datasetUid: string,
  wrapHeaders: boolean,
  depth: number,
): ColGroupDef {
  const isGroup = parts.length > 1;
  const colDef: any = {};

  if (isGroup) {
    colDef.groupId = parts[0];
    colDef.headerName = parts[0];
    colDef.headerClass = pivotColumnLabelsClassFunction;
    colDef.context = {
      pivotHeaderColumn: agRequest.pivotCols[depth]?.field,
      pivotAllowed: true,
      exporting: false,
    } satisfies CustomColDefContext;
  } else {
    const valueCol = agRequest.valueCols.find((r) => r.field === parts[0]);
    if (valueCol) {
      colDef.colId = colId;
      colDef.headerName = valueCol.displayName;
      colDef.headerTooltip = valueCol.displayName;
      colDef.field = colId;
      colDef.context = {
        pivotValueColumn: valueCol.field,
        exporting: false,
        pivotAllowed: true,
        aggFunc: valueCol.aggFunc,
      } satisfies CustomColDefContext;
    }
    colDef.headerClass = headerClassFunction;
  }

  if (wrapHeaders) {
    colDef.wrapHeaderText = true;
    colDef.autoHeaderHeight = true;
  }
  colDef.cellRenderer = ValueRenderer;
  colDef.cellRendererParams = {
    datasetUid,
    wrapWithDivElement: true,
  };
  colDef.cellStyle = cellStyleFunction;
  colDef.tooltipComponentParams = {
    datasetUid,
  };
  colDef.tooltipValueGetter = tooltipValueGetterFunction;
  colDef.valueGetter = valueGetterFunction;
  colDef.suppressHeaderMenuButton = true;
  colDef.headerValueGetter = headerValueGetterFunction;
  return colDef;
}
